import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { confirmCode, resendCode, getCodeInfo } from '../api';
import styles from './HomePage.module.css';

const ConfirmCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const email = (location.state as { email?: string })?.email || '';

  const navigateToSection = (sectionId: string): void => {
    navigate(`/#${sectionId}`);
  };

  const startNewChat = (): void => {
    navigate('/chat');
  };

  const handleConfirm = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!email) {
      setError('Email не указан. Вернитесь к входу или регистрации.');
      navigate('/login');
      return;
    }
    try {
      const response = await confirmCode(email, code);
      if (response.success) {
        navigate('/login'); // После успешного подтверждения сразу в чат
      } else {
        setError(response.message || 'Неверный код');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Неверный код подтверждения');
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!email) {
      setError('Email не указан. Вернитесь к входу или регистрации.');
      navigate('/login');
      return;
    }
    try {
      const response = await resendCode(email);
      if (response.success) {
        setResendMessage('Новый код отправлен на ваш email');
        setError(null);
        setTimeLeft(response.time_left || 60);
      } else {
        setResendMessage(response.message || 'Ошибка при повторной отправке');
        setTimeLeft(response.time_left || 0);
      }
    } catch (err: any) {
      setResendMessage(err.response?.data?.message || 'Ошибка при повторной отправке кода');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchCodeInfo = async () => {
      if (!email) return;
      try {
        const data = await getCodeInfo(email);
        setTimeLeft(data.time_left);
      } catch (err: any) {
        console.error('Ошибка получения информации о коде:', err);
        setTimeLeft(0);
      }
    };

    if (email) {
      fetchCodeInfo();
    } else {
      setError('Email не указан. Вернитесь к входу.');
      navigate('/login');
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  return (
    <div className={styles.homeContainer}>
      <header className={styles.homeHeader}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <div className={styles.homeLogo}>
              <h1>
                GPT4Chats<span>.</span>
              </h1>
            </div>
            <nav className={styles.homeNav}>
              <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                Главная
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                Особенности
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                Вопросы
              </button>
              <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                Начать
              </button>
            </nav>
          </div>
        </div>
      </header>

      <section className={styles.authSection}>
        <div className={styles.authContainer}>
          <h1>Подтверждение Email</h1>
          <p className={styles.authIntro}>
            Введите код, отправленный на {email || 'ваш email'}, чтобы завершить вход.
          </p>
          <form onSubmit={handleConfirm} className={styles.authForm}>
            <div>
              <label htmlFor="code">Код подтверждения</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                required
                className={styles.authInput}
                placeholder="Введите 6-значный код"
              />
            </div>
            {error && <p className={styles.authError}>{error}</p>}
            {resendMessage && <p className={styles.authIntro}>{resendMessage}</p>}
            <button type="submit" className={styles.authButton}>
              Подтвердить
            </button>
          </form>
          <div className={styles.policyFooter}>
            <button
              className={styles.authLink}
              onClick={handleResend}
              disabled={timeLeft > 0}
            >
              Отправить код повторно {timeLeft > 0 ? `(${Math.ceil(timeLeft)} сек)` : ''}
            </button>
            <span className={styles.authSep}>•</span>
            <button className={styles.authLink} onClick={() => navigate('/login')}>
              Вернуться ко входу
            </button>
          </div>
        </div>
      </section>

      <footer className={styles.homeFooter}>
        <div className={styles.container}>
          <div className={styles.footerContent}>
            <div className={styles.footerSection}>
              <h4>
                GPT4Chats<span>.</span>
              </h4>
              <p>Ваш ИИ-помощник для работы и творчества.</p>
              <p>GPT4Chats.ru</p>
            </div>
            <div className={styles.footerSection}>
              <h4>Навигация</h4>
              <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                Главная
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                Особенности
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                Вопросы
              </button>
            </div>
            <div className={styles.footerSection}>
              <h4>Документы</h4>
              <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                Политика конфиденциальности
              </button>
              <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                Условия использования
              </button>
            </div>
            <div className={styles.footerSection}>
              <h4>Контакты</h4>
              <p>Email: support@gpt4chats.ru</p>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConfirmCodePage;